import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Mail, MapPin } from 'lucide-react';
import SEO from '@/components/SEO';

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error('Please fill in all fields');
      return;
    }
    setLoading(true);
    try {
      await api.post('/contact', form);
      toast.success('Message sent! We\'ll get back to you soon.');
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="py-16 lg:py-24">
      <SEO
        title="Contact Us — Meet For Mahjong"
        description="Get in touch with the Meet For Mahjong team. Questions about events, instructors, play locations, the discussion board, or your membership? We're here to help."
        path="/contact"
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-sm font-bold uppercase tracking-[0.2em] text-[#df5681]">Contact</span>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mt-3 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Get in Touch
          </h1>
          <p className="text-slate-500 text-lg">Have a question or feedback? We'd love to hear from you.</p>
          {/* Subtle social-direct CTA — visitors arriving on /contact are
              looking for the fastest way to reach us; surfacing the social
              handles here converts higher than any other page. */}
          <p className="mt-4 text-sm text-slate-500">
            <span className="mr-1.5" aria-hidden="true">💬</span>
            Stay connected with us on{' '}
            <a
              href="https://www.facebook.com/meetformahjong"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#df5681] font-medium hover:underline"
              data-testid="contact-facebook-link"
            >
              Facebook
            </a>
            {' and '}
            <a
              href="https://www.instagram.com/meetformahjong"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#df5681] font-medium hover:underline"
              data-testid="contact-instagram-link"
            >
              Instagram
            </a>
            .
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Contact Info */}
          <div className="space-y-6">
            {[
              { icon: Mail, title: 'Email', text: 'meetformahjong@gmail.com', color: '#56dfb4' },
              { icon: MapPin, title: 'Location', text: 'Nationwide, United States', color: '#df5681' },
            ].map(item => (
              <div key={item.title} className="flex items-start gap-4 p-5 rounded-2xl bg-white border border-slate-100">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${item.color}15` }}>
                  <item.icon className="w-5 h-5" style={{ color: item.color }} />
                </div>
                <div>
                  <h3 className="font-medium text-sm mb-0.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.title}</h3>
                  <p className="text-slate-500 text-sm">{item.text}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="p-8 rounded-2xl bg-white border border-slate-100 shadow-sm space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm mb-1.5 block">Name</Label>
                  <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Your name" className="rounded-xl" data-testid="contact-name-input" />
                </div>
                <div>
                  <Label className="text-sm mb-1.5 block">Email</Label>
                  <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="your@email.com" className="rounded-xl" data-testid="contact-email-input" />
                </div>
              </div>
              <div>
                <Label className="text-sm mb-1.5 block">Subject</Label>
                <Input value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} placeholder="What's this about?" className="rounded-xl" data-testid="contact-subject-input" />
              </div>
              <div>
                <Label className="text-sm mb-1.5 block">Message</Label>
                <Textarea value={form.message} onChange={e => setForm({...form, message: e.target.value})} placeholder="Tell us more..." rows={5} className="rounded-xl" data-testid="contact-message-input" />
              </div>
              <Button type="submit" disabled={loading} className="rounded-full bg-[#df5681] text-white hover:bg-[#c44570] px-8 py-3" data-testid="contact-submit-btn">
                {loading ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
